import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Trophy, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth/auth-store";
import { fetchWeeklyLeaderboard } from "../api";

interface LeaderboardPlayer {
  username: string;
  weeklyXp: number;
  rank: number;
}

interface LeaderboardData {
  players: LeaderboardPlayer[];
  yourRank: number | null;
  yourXp: number;
}

const MEDAL: Record<number, string> = { 1: "\uD83E\uDD47", 2: "\uD83E\uDD48", 3: "\uD83E\uDD49" };

export const WeeklyLeaderboard = () => {
  const { t } = useTranslation();
  const token = useAuthStore((s) => s.token);
  const username = useAuthStore((s) => s.user?.username);

  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetchWeeklyLeaderboard(token)
      .then(setData)
      .catch(() => {
        // silently fail — leaderboard is non-critical
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) return null;

  if (loading) {
    return (
      <Card className="glass p-5 border-secondary/20">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  if (!data || data.players.length === 0) return null;

  const userInTop10 = data.players.some((p) => p.username === username);

  return (
    <section>
      <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
        <Trophy className="w-5 h-5 text-yellow-500" />
        {t("learn_weekly_ranking")}
      </h2>

      <Card className="glass border-secondary/20 overflow-hidden">
        <div className="divide-y divide-muted/30">
          {data.players.map((player) => {
            const isCurrentUser = player.username === username;
            const medal = MEDAL[player.rank];

            return (
              <div
                key={player.rank}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 transition-colors",
                  isCurrentUser &&
                    "glass border border-secondary/40 bg-secondary/10",
                )}
              >
                {/* Rank */}
                <div className="w-8 text-center shrink-0">
                  {medal ? (
                    <span className="text-lg">{medal}</span>
                  ) : (
                    <span className="text-sm font-bold text-muted-foreground">
                      {player.rank}
                    </span>
                  )}
                </div>

                {/* Username */}
                <span
                  className={cn(
                    "flex-1 text-sm font-medium truncate",
                    isCurrentUser && "text-secondary font-bold",
                  )}
                >
                  {player.username}
                  {isCurrentUser && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({t("learn_your_rank")})
                    </span>
                  )}
                </span>

                {/* XP */}
                <span
                  className={cn(
                    "text-sm font-semibold shrink-0",
                    player.rank <= 3
                      ? "text-yellow-500"
                      : "text-muted-foreground",
                  )}
                >
                  {player.weeklyXp} XP
                </span>
              </div>
            );
          })}
        </div>

        {/* Show current user's rank at bottom if not in top 10 */}
        {!userInTop10 && data.yourRank !== null && (
          <>
            <div className="px-4 py-1 flex items-center gap-2 text-muted-foreground">
              <span className="text-xs">...</span>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 glass border-t border-secondary/40 bg-secondary/10">
              <div className="w-8 text-center shrink-0">
                <span className="text-sm font-bold text-muted-foreground">
                  {data.yourRank}
                </span>
              </div>
              <span className="flex-1 text-sm font-bold text-secondary truncate">
                {username}
                <span className="text-xs text-muted-foreground ml-1">
                  ({t("learn_your_rank")})
                </span>
              </span>
              <span className="text-sm font-semibold text-muted-foreground shrink-0">
                {data.yourXp} XP
              </span>
            </div>
          </>
        )}
      </Card>

      {/* XP this week summary */}
      {data.yourRank !== null && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {t("learn_xp_this_week")}: {data.yourXp} XP
        </p>
      )}
    </section>
  );
};
